import { Drawer, Group, Title } from '@mantine/core'
import { useInterval } from '@mantine/hooks'
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table'
import { useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useApplicationContext } from '@/providers/ApplicationContext'
import { useGetCameraList } from '@/queries/cameraQueries'
import { useGetDeviceList } from '@/queries/deviceQueries'
import {
  useGetEventListFilters,
  useGetEventsInfinite
} from '@/queries/eventQueries'
import { CrudAction, EventAction, Resource } from '@/types/permissions'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import { DataUsageIndicator } from '../subscriptions/DataUsageIndicator/DataUsageIndicator'
import { DataUsageIndicatorWrapper } from '../subscriptions/DataUsageIndicator/DataUsageIndicatorWrapper'
import { useFeatureUsage } from '../subscriptions/useFeatureUsage'
import { PlatformAlert } from '../ui-shared/PlatformAlert/PlatformAlert'
import { EventDetail } from './EventDetail/EventDetail'
import { EventsFilterOptions, EventsTable } from './EventsList/EventsTable'
import { useEventIdParam } from './EventsList/useEventIdParam'
import { useTrackingIdParam } from './EventsList/useTrackingIdParam'
import { buildSortingAndFilterParams } from './helpers/buildSortingAndFilterParams'
import { buildTagFilterGroups } from './helpers/buildTagFilterGroups'
import { formatTagName } from './helpers/formatTagName'

const POLL_INTERVAL = 10000

export const EventsPage = () => {
  const intl = useIntl()
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [alertsOnly, setAlertsOnly] = useState(false)
  const [descriptionSearch, setDescriptionSearch] = useState('')
  const [eventDetailsId, setEventDetailsId] = useEventIdParam()
  const [trackingId, setTrackingId] = useTrackingIdParam()

  const { application } = useApplicationContext()

  const { usage } = useFeatureUsage('event_storage')

  const [canDeleteEvents] = useHasPermissions(Resource.Events, [
    CrudAction.Delete
  ])

  const [canDownloadEvents, canAddNotes] = useHasPermissions(Resource.Events, [
    EventAction.Download,
    EventAction.AddNote
  ])

  const sortingAndFilterParams = buildSortingAndFilterParams({
    sorting,
    columnFilters,
    tagSources: alertsOnly ? ['vp_agent_alert'] : undefined,
    descriptionSearch: descriptionSearch || undefined,
    trackingIds: trackingId || undefined
  }) as Record<string, string>

  const {
    data: eventsData,
    fetchNextPage,
    isFetching,
    refetch
  } = useGetEventsInfinite(application?.id || '', sortingAndFilterParams)

  const { start, stop, active } = useInterval(() => {
    if (!isFetching) {
      void refetch()
    }
  }, POLL_INTERVAL)

  useEffect(() => {
    if (eventsData && !active) {
      start()
    }
  }, [eventsData, active, start])

  // Cleanup function to stop the interval when the component is unmounted
  useEffect(() => {
    return stop
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: deviceListData } = useGetDeviceList({
    limit: MAX_ITEMS_WITHOUT_PAGINATION,
    ordering: 'label'
  })
  const { data: cameraListData } = useGetCameraList({
    limit: MAX_ITEMS_WITHOUT_PAGINATION,
    ordering: 'camera_name'
  })
  const { data: eventListFiltersData } = useGetEventListFilters(
    application?.id || '',
    alertsOnly ? ['vp_agent_alert'] : undefined
  )

  const events = eventsData?.pages?.flatMap((page) => page?.results) || []

  const deviceOptions = useMemo(
    () =>
      (deviceListData?.results ?? []).map(({ id, label }) => ({
        value: id,
        label
      })),
    [deviceListData]
  )

  const cameraOptions = useMemo(
    () =>
      (cameraListData?.results ?? []).map(({ camera_id, camera_name }) => ({
        value: camera_id,
        label: camera_name
      })),
    [cameraListData]
  )

  const filterOptions: EventsFilterOptions = useMemo(
    () => ({
      devices: deviceOptions,
      cameras: cameraOptions,
      detections: eventListFiltersData?.detections ?? [],
      roiNames: [
        ...(eventListFiltersData?.roi_names ?? []),
        ...(eventListFiltersData?.line_names ?? [])
      ],
      classifierTags: (eventListFiltersData?.tags ?? [])
        .filter(({ tag_source }) => tag_source === 'classifier')
        .map(({ tag_name }) => ({
          value: tag_name,
          label: formatTagName(tag_name)
        })),
      nonClassifierTags: buildTagFilterGroups(
        eventListFiltersData?.tags ?? [],
        intl.formatMessage
      )
    }),
    [deviceOptions, cameraOptions, eventListFiltersData, intl]
  )

  return (
    <>
      <Group justify="space-between" align="center" mb="md">
        <Title order={2}>
          <FormattedMessage id="events.title" />
        </Title>

        {usage && !usage.isUnlimited && (
          <DataUsageIndicatorWrapper maw={320}>
            <DataUsageIndicator
              title={<FormattedMessage id="subscriptions.usage.eventStorage" />}
              tooltip={
                <FormattedMessage id="subscriptions.usage.eventStorage.description" />
              }
              used={usage.used}
              total={usage.limit}
              unit={usage.unit}
            />
          </DataUsageIndicatorWrapper>
        )}
      </Group>

      {usage && !usage.isUnlimited && usage.used >= usage.limit && (
        <PlatformAlert variant="warning" mb="xs" compact>
          <FormattedMessage id="subscriptions.usage.eventStorage.warning" />
        </PlatformAlert>
      )}

      <EventsTable
        events={events}
        totalEvents={eventsData?.pages[0]?.count || 0}
        applicationId={application?.id || ''}
        isFetching={isFetching}
        sorting={sorting}
        columnFilters={columnFilters}
        filterOptions={filterOptions}
        showDeleteButton={canDeleteEvents}
        showDownloadButton={canDownloadEvents}
        showEventNote={canAddNotes}
        fetchNextPage={() => void fetchNextPage()}
        refetch={() => void refetch()}
        alertsOnly={alertsOnly}
        descriptionSearch={descriptionSearch}
        trackingId={trackingId}
        onSortingChange={setSorting}
        onFilterChange={setColumnFilters}
        onRowClick={(eventId) => setEventDetailsId(eventId)}
        onAlertsOnlyChange={setAlertsOnly}
        onDescriptionSearchChange={setDescriptionSearch}
        onTrackingIdChange={setTrackingId}
      />

      <Drawer
        position="right"
        size="80%"
        opened={eventDetailsId !== null}
        withCloseButton={false}
        transitionProps={{
          transition: 'fade-left',
          duration: 100
        }}
        onClose={() => setEventDetailsId(null)}
      >
        {eventDetailsId && (
          <EventDetail
            appId={application?.id || ''}
            eventId={eventDetailsId}
            trackingIds={trackingId || undefined}
            onBackClick={() => setEventDetailsId(null)}
          />
        )}
      </Drawer>
    </>
  )
}
